// Context-aware prompt suggestions (ipa-training-mode).
// When the widget knows what page the user is on, it swaps the generic
// suggestion chips for page-context-aware ones. The chips show on the empty
// screen only: once the first message is sent they go away (FR31, IP-16954);
// replies that offer next steps carry their own buttons. This applies to ALL
// users regardless of training mode — training mode gates the prompt coach and
// the nudge banner only.
import { translate } from '@utils/i18n';
import { PROMPT_SUGGESTIONS, TECH_PORTAL_PROMPT_SUGGESTIONS } from './prompts';
const I18N_NAMESPACE = 'features.ai_chat_widget.contextual_suggestions';
function promptSuggestion(key) {
    const prompt = translate(`prompts.${key}`, { namespace: I18N_NAMESPACE });
    return { label: prompt, prompt };
}
// ---------------------------------------------------------------------------
// Context-aware suggestions keyed by page type.
// Prompts deliberately use "this building" / "this inspection" — page context
// grounding means the assistant already knows which entity the user is viewing,
// and the chips should model that pattern for users. Contextual chips display
// the exact prompt they send, matching the default suggestion behavior.
//
// Coverage: entity show pages, building-nested pages, iOS-native pages, AND
// listing/index pages. The bar for every chip: it must ask for something the
// page does NOT already make obvious — synthesis, cross-referencing, drafting,
// or an action. Chips that merely restate a headline fact the page already
// shows (e.g. "is this invoice paid?", "what's the status?") are intentionally
// avoided. Page types with no clear value-add (e.g. `dashboard`, `map`,
// `calendar`) fall back to the host's default PROMPT_SUGGESTIONS.
//
// Built lazily (rather than as a module-level constant) so translate() always
// resolves against the current locale at call time.
// ---------------------------------------------------------------------------
/* eslint-disable @typescript-eslint/naming-convention -- keys mirror page_type wire values */
function buildContextualSuggestions() {
    return {
        // ---- Entity show pages ----
        building: [
            promptSuggestion('building.open_deficiencies'),
            promptSuggestion('building.next_inspection_due'),
            promptSuggestion('building.list_devices')
        ],
        inspection: [
            promptSuggestion('inspection.summarize_failed_items'),
            promptSuggestion('inspection.deficiencies_found'),
            promptSuggestion('inspection.create_work_order')
        ],
        work_order: [promptSuggestion('work_order.remaining_to_close'), promptSuggestion('work_order.draft_status_update')],
        proposal: [promptSuggestion('proposal.review_flag_unusual'), promptSuggestion('proposal.draft_followup_email')],
        account: [promptSuggestion('account.list_buildings'), promptSuggestion('account.open_invoices')],
        report: [promptSuggestion('report.summarize_findings'), promptSuggestion('report.list_failed_items')],
        inspection_series: [
            promptSuggestion('inspection_series.upcoming_inspections'),
            promptSuggestion('inspection_series.summarize_history')
        ],
        deficiency: [promptSuggestion('deficiency.explain_resolve'), promptSuggestion('deficiency.draft_correction')],
        contact: [promptSuggestion('contact.list_buildings')],
        invoice: [
            promptSuggestion('invoice.explain_line_items'),
            promptSuggestion('invoice.draft_payment_reminder'),
            promptSuggestion('invoice.other_open_invoices')
        ],
        device: [promptSuggestion('device.inspection_history'), promptSuggestion('device.explain_device')],
        // ---- Building-scoped nested pages ----
        building_systems: [promptSuggestion('building_systems.list_systems')],
        building_assets: [promptSuggestion('building_assets.list_assets')],
        building_equipments: [promptSuggestion('building_equipments.list_equipment')],
        building_inspections: [
            promptSuggestion('building_inspections.overdue_inspections'),
            promptSuggestion('building_inspections.recent_inspections')
        ],
        building_inspection_series: [promptSuggestion('building_inspection_series.list_series')],
        building_contacts: [promptSuggestion('building_contacts.list_contacts')],
        building_deficiencies: [promptSuggestion('building_deficiencies.open_deficiencies')],
        building_reports: [promptSuggestion('building_reports.recent_reports')],
        // ---- iOS-native (v2) page types ----
        alarm_system: [promptSuggestion('alarm_system.summarize_devices')],
        alarm_device: [promptSuggestion('alarm_device.explain_device')],
        control_panel: [promptSuggestion('control_panel.summarize_controls')],
        visit: [promptSuggestion('visit.summarize_visit')],
        // ---- Listing / index pages ----
        // List-scoped prompts: filter, prioritize, and summarize ACROSS the list the
        // user is looking at — the questions someone actually has when staring at a
        // grid, distinct from the single-entity questions above.
        buildings_index: [
            promptSuggestion('buildings_index.open_deficiencies'),
            promptSuggestion('buildings_index.overdue_inspections'),
            promptSuggestion('buildings_index.due_this_month')
        ],
        inspections_index: [
            promptSuggestion('inspections_index.failed_items'),
            promptSuggestion('inspections_index.due_this_month'),
            promptSuggestion('inspections_index.summarize_recent')
        ],
        work_orders_index: [
            promptSuggestion('work_orders_index.open_orders'),
            promptSuggestion('work_orders_index.overdue_orders'),
            promptSuggestion('work_orders_index.breakdown_by_status')
        ],
        proposals_index: [
            promptSuggestion('proposals_index.awaiting_approval'),
            promptSuggestion('proposals_index.total_open_value')
        ],
        accounts_index: [
            promptSuggestion('accounts_index.open_invoices'),
            promptSuggestion('accounts_index.upcoming_inspections')
        ],
        reports_index: [promptSuggestion('reports_index.awaiting_delivery'), promptSuggestion('reports_index.most_recent')],
        inspection_series_index: [
            promptSuggestion('inspection_series_index.coming_due_soon'),
            promptSuggestion('inspection_series_index.overdue')
        ],
        ai_to_review_inspections: [
            promptSuggestion('ai_to_review_inspections.summarize_waiting'),
            promptSuggestion('ai_to_review_inspections.prioritize')
        ],
        deficiencies_index: [
            promptSuggestion('deficiencies_index.all_open'),
            promptSuggestion('deficiencies_index.most_critical'),
            promptSuggestion('deficiencies_index.group_by_building')
        ],
        contacts_index: [promptSuggestion('contacts_index.find_by_name'), promptSuggestion('contacts_index.missing_email')],
        invoices_index: [
            promptSuggestion('invoices_index.all_overdue'),
            promptSuggestion('invoices_index.total_outstanding'),
            promptSuggestion('invoices_index.sent_this_month')
        ],
        alarm_devices_index: [
            promptSuggestion('alarm_devices_index.due_for_inspection'),
            promptSuggestion('alarm_devices_index.breakdown_by_type')
        ],
        control_panels_index: [
            promptSuggestion('control_panels_index.due_for_inspection'),
            promptSuggestion('control_panels_index.group_by_building')
        ]
    };
}
/* eslint-enable @typescript-eslint/naming-convention */
// Resolve page type across both context schemas (v1 camelCase / v2 snake_case)
export function resolvePageType(ctx) {
    if (!ctx)
        return undefined;
    const v2PageType = ctx.page_type;
    if (typeof v2PageType === 'string')
        return v2PageType;
    return ctx.pageType;
}
// Design choice: chips are scoped to the PAGE, not the conversation turn —
// they stay visible/stable for as long as the user is on this page context,
// rather than being regenerated after every assistant turn. Published
// guidance (NN/g) mostly discusses turn-by-turn follow-up chips; page-scoped
// persistence is a deliberate, slightly-ahead-of-evidence call so a chip the
// user glanced at but didn't click doesn't vanish the moment they send a
// message. Revisit if usage data says otherwise.
/**
 * Resolve the suggestion chips to show for the current page context.
 * Falls back to the host's default suggestions when there is no page
 * context, the page type is unrecognized, or the page type has no curated
 * chips (e.g. dashboards/listings with no single obvious next question).
 *
 * Independent of training mode — every user gets contextual chips.
 */
export function getContextualSuggestions(ctx, host) {
    var _a;
    const fallback = host === 'technician_portal' ? TECH_PORTAL_PROMPT_SUGGESTIONS : PROMPT_SUGGESTIONS;
    const pageType = resolvePageType(ctx);
    if (!pageType)
        return fallback;
    return (_a = buildContextualSuggestions()[pageType]) !== null && _a !== void 0 ? _a : fallback;
}
// Human-readable noun phrase per page type, used to build an accessible,
// context-naming label for the chip group (e.g. "Suggested questions for
// this building"). Only needed for page types with curated chips above.
/* eslint-disable @typescript-eslint/naming-convention -- keys mirror page_type wire values */
function buildPageTypeLabels() {
    const noun = (key) => translate(`page_nouns.${key}`, { namespace: I18N_NAMESPACE });
    return {
        building: noun('building'),
        inspection: noun('inspection'),
        work_order: noun('work_order'),
        proposal: noun('proposal'),
        account: noun('account'),
        report: noun('report'),
        inspection_series: noun('inspection_series'),
        deficiency: noun('deficiency'),
        contact: noun('contact'),
        invoice: noun('invoice'),
        device: noun('device'),
        building_systems: noun('building_systems'),
        building_assets: noun('building_assets'),
        building_equipments: noun('building_equipments'),
        building_inspections: noun('building_inspections'),
        building_inspection_series: noun('building_inspection_series'),
        building_contacts: noun('building_contacts'),
        building_deficiencies: noun('building_deficiencies'),
        building_reports: noun('building_reports'),
        alarm_system: noun('alarm_system'),
        alarm_device: noun('alarm_device'),
        control_panel: noun('control_panel'),
        visit: noun('visit'),
        // Listing / index pages — plural noun reads naturally after "…for".
        buildings_index: noun('buildings_index'),
        inspections_index: noun('inspections_index'),
        work_orders_index: noun('work_orders_index'),
        proposals_index: noun('proposals_index'),
        accounts_index: noun('accounts_index'),
        reports_index: noun('reports_index'),
        inspection_series_index: noun('inspection_series_index'),
        ai_to_review_inspections: noun('ai_to_review_inspections'),
        deficiencies_index: noun('deficiencies_index'),
        contacts_index: noun('contacts_index'),
        invoices_index: noun('invoices_index'),
        alarm_devices_index: noun('alarm_devices_index'),
        control_panels_index: noun('control_panels_index')
    };
}
/* eslint-enable @typescript-eslint/naming-convention */
/**
 * Accessible group label for the chip row, naming the current page context
 * when known (e.g. "Suggested questions for this building") so screen-reader
 * users get the same grounding sighted users get from the page itself.
 */
export function getContextualSuggestionsLabel(ctx) {
    const pageType = resolvePageType(ctx);
    const noun = pageType ? buildPageTypeLabels()[pageType] : undefined;
    return noun
        ? translate('group_label_for_context', { namespace: I18N_NAMESPACE, noun })
        : translate('prompt_suggestions.default_group_label', { namespace: 'features.ai_chat_widget' });
}
